import type { Options } from 'browser-image-compression';
import imageCompression from 'browser-image-compression';
import { saveAs } from 'file-saver';

import { usePostSWRFetcher } from 'data/base-api';
import type useHandleError from 'hooks/useHandleError';
import { checkStatus, get, post } from 'libs/apiClient';

import type { GeoCoordinate } from '../model/ImageMap/types';

export const baseFileUrl = '/api/file';

const compressionOptions: Options = {
  useWebWorker: true,
  initialQuality: 0.51,
  maxWidthOrHeight: 1920,
  preserveExif: true,
};

const compressImage = async (f: File): Promise<File> => {
  if (!f.type.includes('image')) return f;

  try {
    return new File([await imageCompression(f, compressionOptions)], f.name);
  } catch {
    // preserveExif fails on some JPEGs — retry without it
    try {
      return new File(
        [
          await imageCompression(f, {
            ...compressionOptions,
            preserveExif: false,
          }),
        ],
        f.name
      );
    } catch {
      // Compression not possible, upload original
      return f;
    }
  }
};

export interface FileUploadParams {
  accessType?: 'internal' | 'external';
  collectionRef?: string;
  idRef?: string;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
}

type FileHeader = Omit<FileUploadParams, 'collectionRef' | 'idRef'> & {
  CollectionRef?: string;
  IdRef?: string;
};

interface Response {
  files: DitioFile[];
  message: string;
  successfull: boolean;
}

export interface DitioFile {
  created: string;
  fileReference: {
    collectionRef: null | string;
    companyId: string;
    companyIds: string[];
    createdBy: string;
    createdDateTime: string;
    downloadUrl: string;
    fileName: string;
    fileNameOrig: string;
    fileSize: number;
    fileType: string;
    geoCoordinate: GeoCoordinate;
    id: string;
    idRef: null | string;
    isImage: boolean;
    secondaryIdRefs: string[];
    userSecurityLevel: number; // public int UserSecurityLevel { get; set; } - not enum
  };
  modified: string;
  name: string;
  size: number;
  url: string;
}

export interface ShortFileReference {
  id: string;
  createdBy: string;
  createdDateTime: string;
  fileName: string;
  fileNameOrig: string;
  fileType: string;
  geoCoordinate: { latitude: number; longitude: number; accuracy: number };
  accuracy: number;
  latitude: number;
  longitude: number;
}

export type FileMetadata = DitioFile['fileReference'] & {
  createdByUserName: string;
};

interface UploadFileArgs {
  file: File | File[];
  params?: FileUploadParams;
  uploadUrl?: string;
}

export const uploadFile = async <R = Response>({
  file,
  params,
  uploadUrl,
}: UploadFileArgs): Promise<R> => {
  const data = new FormData();

  if (Array.isArray(file)) {
    const processedFiles = await Promise.all(file.map((f) => compressImage(f)));

    processedFiles.forEach((processedFile) => {
      data.append('files', processedFile);
    });
  } else {
    data.append('file', await compressImage(file));
  }

  let fileHeaders: FileHeader | null = null;

  // idRef could be null e.g. image is uploaded before alert was created
  if (params && params.collectionRef && params.idRef !== undefined) {
    const { accessType, collectionRef, idRef, latitude, longitude, accuracy } =
      params;

    fileHeaders = {
      accessType,
      CollectionRef: collectionRef,
      IdRef: idRef,
      latitude,
      longitude,
      accuracy,
    };
  }

  const response = checkStatus(
    await post(uploadUrl || baseFileUrl, data, fileHeaders, true)
  );
  const parsed = await response.json();

  return parsed;
};

/** Download a file from a provided URL */
export const downloadFile = async (
  fileUrl: string,
  handleError: ReturnType<typeof useHandleError>
) => {
  try {
    const isFileSaverSupported = !!new Blob();
  } catch (e) {
    alert('This feature is not supported by your browser.');
  } finally {
    try {
      const response = await get(fileUrl);
      const blob = await response.blob();
      saveAs(blob, `${fileUrl.split('/').pop()}.${blob.type.split('/').pop()}`);
    } catch (e) {
      handleError(e);
    }
  }
};

export const useGetFilesMetadata = (fileIds: string[]) =>
  usePostSWRFetcher<FileMetadata[]>(
    fileIds.length ? `/api/filereference/metadata` : null,
    fileIds
  );

export const downloadFilesWithMetaData = async (
  fileIds: string[],
  includeMetaData: boolean,
  handleError: ReturnType<typeof useHandleError>,
  fileName?: string
) => {
  try {
    const response = checkStatus(
      await post(
        includeMetaData
          ? '/api/filereference/download?writeMetaDataInImage=true'
          : '/api/filereference/download',
        fileIds
      )
    );
    const blob = await response.blob();
    saveAs(blob, `${fileName ?? 'downloaded-files'}.zip`);
  } catch (e) {
    handleError(e);
  }
};
